/**
 * Pattern of Life API Service
 * Provides access to PoL dashboard data, patterns, correlations, and anomalies.
 */

import { getPolApiClient } from '../polApi';

// Resolve the analyst's IANA timezone (e.g. "America/New_York") so backend
// hour-of-day / day-of-week bucketing on the polar wheel can convert the
// stored-as-UTC timestamps into local wall-clock. Falls back to UTC when the
// runtime has no Intl support so the request still succeeds.
function getLocalIanaTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// Types
export interface EntitySummary {
  entity_id: string;
  entity_type: string;
  routine_score: number;
  mobility_score: number;
  social_score: number;
  anomaly_score: number;
  total_dwells: number;
  unique_locations: number;
  total_dwell_duration_minutes: number;
  total_routes: number;
  total_distance_km: number;
  total_calls: number;
  total_sms: number;
  unique_contacts: number;
  // Data maturity fields
  days_with_data?: number;
  data_confidence?: number;
  baseline_established?: boolean;
  first_activity_date?: string;
  anomaly_count: number;
  ai_summary: string | null;
  top_locations: LocationVisit[];
  active_hours: number[];
  active_days: string[];
  hourly_heatmap?: { day_of_week: number; hour: number; count: number }[];
}

export interface LocationVisit {
  location_name: string;
  location_id: string;
  latitude: number;
  longitude: number;
  visits: number;
  total_minutes: number;
}

export interface RecurringPattern {
  id: number;
  entity_id: string;
  pattern_type: string;
  status: string;
  location_id: string;
  location_name: string;
  location_coordinates: { lat: number; lon: number };
  frequency: string;
  days_of_week: string[];
  time_window_start: string;
  time_window_end: string;
  occurrence_count: number;
  adherence_rate: number;
  confidence_score: number;
  first_observed: string;
  last_observed: string;
  avg_duration_minutes: number;
  avg_arrival_variance_minutes: number;
}

export interface EntityCorrelation {
  id: number;
  entity_a_id: string;
  entity_b_id: string;
  correlation_type: string;
  correlation_strength: number;
  confidence_score: number;
  occurrence_count: number;
  shared_locations: { name: string; lat: number; lon: number; count: number }[];
  avg_overlap_duration_minutes: number;
  shared_routes: any[];
  avg_time_delta_minutes: number;
  first_observed: string;
  last_observed: string;
  related_entity?: string;
}

export interface PolAnomaly {
  id: number;
  entity_id: string;
  anomaly_type: string;
  severity: string;
  expected_behavior: string;
  actual_behavior: string;
  deviation_score: number;
  anomaly_time: string;
  is_acknowledged: boolean;
  acknowledged_by: string;
  acknowledged_at: string;
  resolution_notes: string;
  detected_at: string;
  /**
   * For point-type anomalies: a single { lat, lon, name } entry.
   * For trip-type anomalies (route_deviation): nested { origin, destination } endpoints.
   * Both shapes are unioned so callers can detect via `'origin' in expected_location`.
   */
  expected_location?: {
    location_id?: string;
    name?: string;
    lat?: number;
    lon?: number;
    origin?: { name?: string; lat?: number; lon?: number };
    destination?: { name?: string; lat?: number; lon?: number };
  };
  actual_location?: {
    location_id?: string;
    name?: string;
    lat?: number;
    lon?: number;
    origin?: { name?: string; lat?: number; lon?: number };
    destination?: { name?: string; lat?: number; lon?: number };
  };
}

export interface LocationSequence {
  id: number;
  entity_id: string;
  sequence_hash: string;
  sequence_length: number;
  locations: { id: string; name: string; lat: number; lon: number; order: number }[];
  occurrence_count: number;
  avg_total_duration_minutes: number;
  avg_transition_times: any;
  typical_start_hour: number;
  typical_days: string[];
  first_observed: string;
  last_observed: string;
}

export interface RoutePattern {
  id: number;
  entity_id: string;
  course_id: string;
  route_hash: string;
  route_name: string;
  device_type?: string;  // phone, watch, vehicle, or mixed
  origin: {
    name: string;
    lat: number | null;
    lon: number | null;
  };
  destination: {
    name: string;
    lat: number | null;
    lon: number | null;
  };
  route_geometry: [number, number][];  // Array of [lon, lat] coordinates
  route_geojson: any;                   // GeoJSON for direct map use
  usage_count: number;
  consistency_score: number;
  confidence_score: number;
  peak_hour: number | null;
  peak_day: string | null;
  typical_hours: number[];
  typical_days: string[];
  avg_distance_meters: number;
  avg_duration_seconds: number;
  first_observed: string;
  last_observed: string;
  pattern_summary: string;
  behavioral_tags: string[];
  trip_instances?: {
    timestamp: string;
    date: string;
    time: string;
    day: string;
    distance_m: number;
    duration_s: number;
    point_count: number;
  }[];
  shared_with_entities?: string[];  // Other entities using same route
  shared_route_count?: number;       // Count of other entities
}

export interface TemporalHeatmapData {
  hour: number;
  day_of_week: number;
  activity_count: number;
  dwell_count?: number;
  call_count?: number;
  sms_count?: number;
  browsing_count?: number;
  app_count?: number;
  route_count?: number;
}

export interface PeriodSummary {
  period_type: string;
  period_start: string;
  period_end: string;
  total_entities: number;
  total_patterns: number;
  total_correlations: number;
  total_anomalies: number;
  total_sequences: number;
  total_atypical_days?: number;
  avg_routine_score: number;
  avg_mobility_score: number;
}

/**
 * Per-entity, per-day rollup produced by Step 12 of the embedding pipeline.
 * `atypicality_score` fuses anomaly counts + mobility deviation into a single
 * "how interesting was this day" scalar for ranking.
 */
export interface DailyActivityMetric {
  entity_id: string;
  course_id: string | null;
  day: string; // ISO datetime
  atypicality_score: number;
  distance_traveled_km: number;
  unique_places_visited: number;
  max_distance_from_home_km: number;
  dwell_count: number;
  total_dwell_minutes: number;
  track_point_count: number;
  anomaly_breakdown: {
    time_shift: number;
    dwell_duration_anomaly: number;
    unusual_location: number;
    routine_break: number;
    route_deviation: number;
    lapse_break: number;
    new_relationship: number;
    ml_anomaly: number;
  };
  total_anomaly_count: number;
  computed_at?: string;
}

export interface DashboardResponse {
  entities: EntitySummary[];
  period_summary: PeriodSummary;
  top_patterns: RecurringPattern[];
  top_correlations: EntityCorrelation[];
  recent_anomalies: PolAnomaly[];
  location_sequences: LocationSequence[];
  temporal_heatmap: TemporalHeatmapData[];
  top_atypical_days?: DailyActivityMetric[];
}

export interface EntityDetailResponse {
  entity_id: string;
  period_start: string;
  period_end: string;
  summaries: any[];
  patterns: RecurringPattern[];
  correlations: EntityCorrelation[];
  sequences: LocationSequence[];
  anomalies: PolAnomaly[];
  timeline: TimelineEvent[];
}

export interface TimelineEvent {
  event_type: string;
  timestamp: string;
  description: string;
  latitude: number;
  longitude: number;
  duration_minutes: number;
}

export interface ProcessingJob {
  id: number;
  course_id: string;
  period_type: string;
  period_start: string;
  period_end: string;
  status: string;
  progress_percentage: number;
  current_step: string;
  entities_processed: number;
  patterns_detected: number;
  correlations_detected: number;
  anomalies_detected: number;
  error_message: string;
  created_at: string;
  completed_at: string;
  processing_duration_seconds: number;
}

// Digital Patterns
export interface DigitalPattern {
  id: number;
  entity_id: string;
  course_id: string;
  category: string;  // app_usage, browsing, communication, location_digital
  name: string;      // App/site name
  description: string;
  pattern_type: string;  // daily, time_specific, day_specific, weekend
  peak_hour: number | null;
  peak_day: number | null;
  time_of_day: string | null;
  occurrence_count: number;
  days_active: number | null;
  usage_frequency: number | null;
  confidence: number;
  location_name: string | null;
  metadata: any;
}

export interface DigitalPatternsResponse {
  patterns: DigitalPattern[];
  total_count: number;
  by_category: Record<string, number>;
  categories: string[];
}

export interface DigitalSimilarity {
  entity_a: string;
  entity_b: string;
  similarity: number;
  common_apps: string[];
}

export interface DigitalSimilarityResponse {
  similarities: DigitalSimilarity[];
  total_pairs: number;
  entities_analyzed: number;
}

// Digital Insights (embedding-powered)
export interface ContributingFactor {
  feature: string;
  actual: number;
  baseline: number;
  pct_change: number;
  direction: 'increase' | 'decrease';
}

export interface DigitalAnomaly {
  entity_id: string;
  date: string;
  anomaly_score: number;
  is_anomalous: boolean;
  reconstruction_error: number;
  raw_features: any;
  total_dwells: number;
  active_hours_count: number;
  contributing_factors?: ContributingFactor[];
}

export interface DigitalEntityProfile {
  entity_id: string;
  avg_anomaly_score: number;
  max_anomaly_score: number;
  anomalous_days: number;
  total_days_scored: number;
  anomaly_rate: number;
}

export interface BehavioralSimilarity {
  entity_a: string;
  entity_b: string;
  similarity: number;
}

export interface DigitalFingerprint {
  course_id?: string | null;
  category: string;
  name: string;
  description: string;
  pattern_type?: string | null;
  occurrence_count: number;
  confidence: number;
  peak_hour: number | null;
  peak_day?: number | string | null;
  time_of_day: string | null;
  days_active?: number | null;
  usage_frequency?: number | null;
  location_name?: string | null;
  metadata?: Record<string, any>;
}

export interface DigitalFingerprintSummary {
  entity_id: string;
  course_id?: string | null;
  primary_apps: string[];
  top_sites: string[];
  communication_profile: string[];
  active_time_windows: string[];
  location_digital_patterns: Array<{
    app: string;
    location?: string | null;
    description: string;
    confidence: number;
  }>;
  confidence_summary: {
    count: number;
    avg: number;
    max: number;
  };
}

export interface AnomalyTimelineEntry {
  date: string;
  avg_score: number;
  entities_scored: number;
  anomalies_detected: number;
}

export interface HourlyEvent {
  type: 'call' | 'sms' | 'app' | 'browsing';
  entity_id: string;
  detail: string;
  contact?: string;
  timestamp: string | null;
  lat?: number | null;
  lon?: number | null;
}

export interface HourlyDetailResponse {
  summary: { calls: number; sms: number; apps: number; browsing: number };
  events: HourlyEvent[];
}

export interface DigitalInsightsResponse {
  anomalies: DigitalAnomaly[];
  entity_profiles: DigitalEntityProfile[];
  behavioral_similarities: BehavioralSimilarity[];
  entity_fingerprints: Record<string, DigitalFingerprint[]>;
  entity_fingerprint_summaries?: Record<string, DigitalFingerprintSummary>;
  anomaly_timeline: AnomalyTimelineEntry[];
  activity_baseline?: number[][];
  activity_counts?: {
    all: number;
    apps: number;
    web: number;
    comms: number;
    location: number;
    calls?: number;
    sms?: number;
  };
  summary: {
    total_entities_scored: number;
    total_anomalies: number;
    total_days_analyzed: number;
    avg_anomaly_rate: number;
  };
}

// Course with date range info
export interface CourseInfo {
  id: string;
  label: string;
  entity_count: number;
  dwell_count: number;
  earliest_date: string | null;
  latest_date: string | null;
}

export interface CoursesResponse {
  courses: CourseInfo[];
  total: number;
}

// API Functions
export class PolService {
  private get http() {
    return getPolApiClient();
  }

  /**
   * Get list of courses with their data date ranges
   */
  async getCourses(): Promise<CoursesResponse> {
    const response = await this.http.get('/pattern-of-life/courses');
    return response.data;
  }

  /**
   * Get comprehensive Pattern of Life dashboard data
   */
  async getDashboard(
    periodType: string = 'weekly',
    daysBack: number = 7,
    courseId?: string,
    entityIds?: string[],
    deviceType?: string,
    startDate?: string,  // ISO date string
    endDate?: string     // ISO date string
  ): Promise<DashboardResponse> {
    const params: Record<string, string> = {
      period_type: periodType,
      // Send the analyst's browser timezone so the polar wheel's hour/day
      // buckets reflect local wall-clock. Without this the backend defaults
      // to America/New_York which is wrong for analysts elsewhere; with it
      // the wheel reads "morning commute is at 7-9a" wherever you sit.
      tz: getLocalIanaTz(),
    };

    // Use date range if provided, otherwise use daysBack
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.days_back = daysBack.toString();
    }

    if (courseId) {
      params.course_id = courseId;
    }
    if (entityIds && entityIds.length > 0) {
      params.entity_ids = entityIds.join(',');
    }
    if (deviceType) {
      params.device_type = deviceType;
    }

    const response = await this.http.get('/pattern-of-life/dashboard', { params });
    return response.data;
  }

  /**
   * Get detailed PoL data for a specific entity
   */
  async getEntityDetail(
    entityId: string,
    periodType: string = 'weekly',
    daysBack: number = 30
  ): Promise<EntityDetailResponse> {
    const params = {
      period_type: periodType,
      days_back: daysBack.toString(),
    };

    const response = await this.http.get(
      `/pattern-of-life/entity/${entityId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Get recurring patterns with optional filters
   * IMPORTANT: Always pass courseId for data isolation between courses
   */
  async getPatterns(
    entityId?: string,
    courseId?: string,
    patternType?: string,
    status: string = 'active',
    minConfidence: number = 0.3,
    limit: number = 50
  ): Promise<{ patterns: RecurringPattern[]; count: number }> {
    const params: Record<string, string> = {
      status,
      min_confidence: minConfidence.toString(),
      limit: limit.toString(),
    };

    // CRITICAL: Filter by course_id to prevent cross-course data leakage
    if (courseId) params.course_id = courseId;
    if (entityId) params.entity_id = entityId;
    if (patternType) params.pattern_type = patternType;

    const response = await this.http.get('/pattern-of-life/patterns', { params });
    return response.data;
  }

  /**
   * Get entity correlations (co-location, co-travel, etc.)
   * IMPORTANT: Always pass courseId for data isolation between courses
   */
  async getCorrelations(
    entityId?: string,
    courseId?: string,
    correlationType?: string,
    minOccurrences: number = 3,
    limit: number = 50
  ): Promise<{ correlations: EntityCorrelation[]; count: number }> {
    const params: Record<string, string> = {
      min_occurrences: minOccurrences.toString(),
      limit: limit.toString(),
    };

    // CRITICAL: Filter by course_id to prevent cross-course data leakage
    if (courseId) params.course_id = courseId;
    if (entityId) params.entity_id = entityId;
    if (correlationType) params.correlation_type = correlationType;

    const response = await this.http.get('/pattern-of-life/correlations', { params });
    return response.data;
  }

  /**
   * Get detected anomalies
   * IMPORTANT: Always pass courseId for data isolation between courses
   */
  async getAnomalies(
    entityId?: string,
    courseId?: string,
    severity?: string,
    acknowledged?: boolean,
    daysBack: number = 30,
    limit: number = 50
  ): Promise<{ anomalies: PolAnomaly[]; count: number }> {
    const params: Record<string, string> = {
      days_back: daysBack.toString(),
      limit: limit.toString(),
    };

    // CRITICAL: Filter by course_id to prevent cross-course data leakage
    if (courseId) params.course_id = courseId;
    if (entityId) params.entity_id = entityId;
    if (severity) params.severity = severity;
    if (acknowledged !== undefined) params.acknowledged = acknowledged.toString();

    const response = await this.http.get('/pattern-of-life/anomalies', { params });
    return response.data;
  }

  /**
   * Get per-entity, per-day mobility/anomaly rollups (Step 12 output).
   * Sorted by atypicality_score DESC by default — surfaces the most "interesting"
   * entity-day combinations for triage.
   *
   * IMPORTANT: Always pass courseId for data isolation between courses.
   */
  async getDailyActivityMetrics(opts: {
    courseId?: string;
    entityId?: string;
    entityIds?: string[];
    daysBack?: number;
    startDate?: string;
    endDate?: string;
    minScore?: number;
    sortBy?: 'atypicality_score' | 'day' | 'distance_traveled_km' | 'unique_places_visited' | 'dwell_count' | 'total_dwell_minutes';
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  } = {}): Promise<{ metrics: DailyActivityMetric[]; count: number }> {
    const params: Record<string, string> = {
      days_back: (opts.daysBack ?? 30).toString(),
      sort_by: opts.sortBy ?? 'atypicality_score',
      sort_dir: opts.sortDir ?? 'desc',
      limit: (opts.limit ?? 100).toString(),
      offset: (opts.offset ?? 0).toString(),
    };
    if (opts.courseId) params.course_id = opts.courseId;
    if (opts.entityId) params.entity_id = opts.entityId;
    if (opts.entityIds && opts.entityIds.length) params.entity_ids = opts.entityIds.join(',');
    if (opts.startDate) params.start_date = opts.startDate;
    if (opts.endDate) params.end_date = opts.endDate;
    if (opts.minScore !== undefined) params.min_score = opts.minScore.toString();

    const response = await this.http.get('/pattern-of-life/daily-activity-metrics', { params });
    return response.data;
  }

  /**
   * Acknowledge an anomaly alert
   */
  async acknowledgeAnomaly(
    anomalyId: number,
    notes?: string
  ): Promise<{ success: boolean; anomaly_id: number }> {
    const params = notes ? { notes } : {};
    const response = await this.http.post(
      `/pattern-of-life/anomalies/${anomalyId}/acknowledge`,
      {},
      { params }
    );
    return response.data;
  }

  /**
   * Get location sequences (A -> B -> C patterns)
   */
  async getLocationSequences(
    entityId?: string,
    minOccurrences: number = 2,
    limit: number = 50
  ): Promise<{ sequences: LocationSequence[]; count: number }> {
    const params: Record<string, string> = {
      min_occurrences: minOccurrences.toString(),
      limit: limit.toString(),
    };

    if (entityId) params.entity_id = entityId;

    const response = await this.http.get('/pattern-of-life/location-sequences', { params });
    return response.data;
  }

  /**
   * Get rich route patterns with GPS geometry for map visualization
   */
  async getRoutePatterns(
    entityId?: string,
    courseId?: string,
    minUsage: number = 2,
    limit: number = 50,
    deviceType?: string,  // phone, watch, vehicle, mixed
    startDate?: string,   // YYYY-MM-DD format
    endDate?: string      // YYYY-MM-DD format
  ): Promise<{ routes: RoutePattern[]; count: number }> {
    const params: Record<string, string> = {
      min_usage: minUsage.toString(),
      limit: limit.toString(),
    };

    if (entityId) params.entity_id = entityId;
    if (courseId) params.course_id = courseId;
    if (deviceType) params.device_type = deviceType;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await this.http.get('/pattern-of-life/route-patterns', { params });
    return response.data;
  }

  /**
   * Get similar routes for comparison (routes shared by multiple entities)
   */
  async getSimilarRoutes(routeId: number): Promise<{
    primary_route_id: number;
    routes: (RoutePattern & { is_primary: boolean })[];
    count: number;
  }> {
    const response = await this.http.get(`/pattern-of-life/route-patterns/${routeId}/similar`);
    return response.data;
  }

  /**
   * Trigger PoL processing for a time period
   */
  async triggerProcessing(
    periodType: string = 'daily',
    daysBack: number = 7,
    courseId?: string,
    entityIds?: string[]
  ): Promise<{ message: string; period_type: string; period_start: string; period_end: string }> {
    const response = await this.http.post('/pattern-of-life/process', {
      period_type: periodType,
      days_back: daysBack,
      course_id: courseId,
      entity_ids: entityIds,
    });
    return response.data;
  }

  /**
   * Get recent processing jobs
   */
  async getProcessingJobs(
    status?: string,
    limit: number = 20
  ): Promise<{ jobs: ProcessingJob[]; count: number }> {
    const params: Record<string, string> = {
      limit: limit.toString(),
    };

    if (status) params.status = status;

    const response = await this.http.get('/pattern-of-life/processing-jobs', { params });
    return response.data;
  }

  /**
   * Get timeline data for Gantt visualization
   */
  async getTimelineData(
    courseId?: string,
    entityIds?: string[],
    daysBack: number = 7,
    startDate?: string,
    endDate?: string
  ): Promise<TimelineResponse> {
    const params: Record<string, string> = {};

    // Use date range if provided, otherwise use daysBack
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.days_back = daysBack.toString();
    }

    if (courseId) params.course_id = courseId;
    if (entityIds && entityIds.length > 0) params.entity_ids = entityIds.join(',');

    const response = await this.http.get('/pattern-of-life/timeline', { params });
    return response.data;
  }

  /**
   * Get track positions at a specific time
   */
  async getTracksAtTime(
    timestamp: string,
    courseId?: string,
    entityIds?: string[],
    windowSeconds: number = 30
  ): Promise<TracksAtTimeResponse> {
    const params: Record<string, string> = {
      timestamp,
      window_seconds: windowSeconds.toString(),
    };

    if (courseId) params.course_id = courseId;
    if (entityIds && entityIds.length > 0) params.entity_ids = entityIds.join(',');

    const response = await this.http.get('/pattern-of-life/tracks-at-time', { params });
    return response.data;
  }

  /**
   * Get digital behavior patterns (app usage, browsing, communication)
   */
  async getDigitalPatterns(
    courseId?: string,
    entityIds?: string[],
    category?: string,
    minConfidence: number = 0.5
  ): Promise<DigitalPatternsResponse> {
    const params: Record<string, string> = {
      min_confidence: minConfidence.toString(),
    };

    if (courseId) params.course_id = courseId;
    if (entityIds && entityIds.length > 0) params.entity_ids = entityIds.join(',');
    if (category) params.category = category;

    const response = await this.http.get('/pattern-of-life/digital-patterns', { params });
    return response.data;
  }

  /**
   * Get students with similar digital behavior profiles
   */
  async getDigitalSimilarity(
    entityId?: string,
    minSimilarity: number = 0.7
  ): Promise<DigitalSimilarityResponse> {
    const params: Record<string, string> = {
      min_similarity: minSimilarity.toString(),
    };

    if (entityId) params.entity_id = entityId;

    const response = await this.http.get('/pattern-of-life/digital-similarity', { params });
    return response.data;
  }

  /**
   * Get real digital PoL insights from embedding-based anomaly detection.
   * Supports explicit start/end dates or days_back (relative).
   */
  async getDigitalInsights(
    courseId?: string,
    entityIds?: string[],
    daysBack: number = 30,
    startDate?: string,
    endDate?: string,
  ): Promise<DigitalInsightsResponse> {
    const params: Record<string, string> = {
      days_back: daysBack.toString(),
    };
    if (courseId) params.course_id = courseId;
    if (entityIds && entityIds.length > 0) params.entity_ids = entityIds.join(',');
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await this.http.get('/pattern-of-life/digital-insights', { params });
    return response.data;
  }

  async getHourlyDetail(
    dayOfWeek: number,
    hour: number,
    courseId?: string,
    daysBack: number = 30,
    entityIds?: string,
    limit?: number,
    startDate?: string,
    endDate?: string,
  ): Promise<HourlyDetailResponse> {
    const params: Record<string, string> = {
      day_of_week: dayOfWeek.toString(),
      hour: hour.toString(),
      days_back: daysBack.toString(),
      // Must match the tz the wheel was rendered in, otherwise the drilldown
      // pulls events from a different local hour than the cell label says.
      tz: getLocalIanaTz(),
    };
    if (courseId) params.course_id = courseId;
    if (entityIds) params.entity_ids = entityIds;
    if (limit !== undefined) params.limit = limit.toString();
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await this.http.get('/pattern-of-life/digital-insights/hourly-detail', { params });
    return response.data;
  }

  async getDigitalEvents(
    courseId?: string,
    daysBack: number = 30,
    entityIds?: string,
    limit?: number,
    startDate?: string,
    endDate?: string,
  ): Promise<HourlyDetailResponse> {
    const params: Record<string, string> = {
      days_back: daysBack.toString(),
    };
    if (courseId) params.course_id = courseId;
    if (entityIds) params.entity_ids = entityIds;
    if (limit !== undefined) params.limit = limit.toString();
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await this.http.get('/pattern-of-life/digital-insights/events', { params });
    return response.data;
  }

  // ============================================================================
  // Embedding & Similarity Search Methods
  // ============================================================================

  /**
   * Find entities with similar behavioral profiles
   * Uses cosine similarity on behavioral embeddings
   */
  async findSimilarEntities(
    entityId: string,
    courseId: string,
    limit: number = 10,
    minSimilarity: number = 0.3
  ): Promise<SimilarEntitiesResponse> {
    const params = {
      course_id: courseId,
      limit: limit.toString(),
      min_similarity: minSimilarity.toString(),
    };

    const response = await this.http.get(
      `/pattern-of-life/similar-entities/${entityId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Find similar trajectory patterns
   */
  async findSimilarTrajectories(
    sequenceId: number,
    courseId: string,
    limit: number = 10,
    minSimilarity: number = 0.5
  ): Promise<SimilarTrajectoriesResponse> {
    const params = {
      course_id: courseId,
      limit: limit.toString(),
      min_similarity: minSimilarity.toString(),
    };

    const response = await this.http.get(
      `/pattern-of-life/similar-trajectories/${sequenceId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Find dwell locations with similar behavioral patterns
   * Uses 64-dimensional location embeddings
   */
  async findSimilarLocations(
    dwellId: number,
    courseId?: string,
    limit: number = 10,
    minSimilarity: number = 0.5
  ): Promise<SimilarLocationsResponse> {
    const params: Record<string, string> = {
      limit: limit.toString(),
      min_similarity: minSimilarity.toString(),
    };
    
    if (courseId) params.course_id = courseId;

    const response = await this.http.get(
      `/pattern-of-life/similar-locations/${dwellId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Trigger dwell embedding computation
   */
  async computeDwellEmbeddings(courseId?: string, limit?: number): Promise<{
    status: string;
    dwell_embeddings_computed: number;
    errors: string[];
  }> {
    const params: Record<string, string> = {};
    if (courseId) params.course_id = courseId;
    if (limit) params.limit = limit.toString();

    const response = await this.http.post('/pattern-of-life/compute-dwell-embeddings', {}, { params });
    return response.data;
  }

  /**
   * Get embedding coverage statistics
   */
  async getEmbeddingStats(courseId?: string): Promise<EmbeddingStats> {
    const params: Record<string, string> = {};
    if (courseId) params.course_id = courseId;

    const response = await this.http.get('/pattern-of-life/embeddings/stats', { params });
    return response.data;
  }

  /**
   * Trigger embedding backfill
   */
  async backfillEmbeddings(courseId?: string): Promise<{ message: string; course_id: string | null }> {
    const params: Record<string, string> = {};
    if (courseId) params.course_id = courseId;

    const response = await this.http.post('/pattern-of-life/embeddings/backfill', {}, { params });
    return response.data;
  }

  // ============================================================================
  // Graph Embedding Methods (Node2Vec - Network Analysis)
  // ============================================================================

  /**
   * Find entities with similar NETWORK positions using graph embeddings
   * Unlike behavioral similarity, this finds entities that:
   * - Are connected to similar groups
   * - Occupy similar roles in the relationship network
   * - Are indirectly connected through shared contacts
   */
  async findSimilarByNetwork(
    entityId: string,
    courseId: string,
    limit: number = 10
  ): Promise<SimilarEntitiesResponse> {
    const params = {
      course_id: courseId,
      limit: limit.toString(),
    };

    const response = await this.http.get(
      `/pattern-of-life/similar-by-network/${entityId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Detect communities/clusters in the entity relationship network
   */
  async getNetworkCommunities(courseId: string): Promise<{
    course_id: string;
    communities: Array<{
      community_id: number;
      size: number;
      members: string[];
      density: number;
      internal_edges: number;
    }>;
    total_communities: number;
  }> {
    const response = await this.http.get('/pattern-of-life/network-communities', {
      params: { course_id: courseId }
    });
    return response.data;
  }

  /**
   * Find entities that bridge different groups (high betweenness centrality)
   */
  async getBridgeEntities(courseId: string, topN: number = 10): Promise<{
    course_id: string;
    bridge_entities: Array<{
      entity_id: string;
      betweenness_score: number;
      degree: number;
    }>;
    count: number;
  }> {
    const response = await this.http.get('/pattern-of-life/bridge-entities', {
      params: { course_id: courseId, top_n: topN.toString() }
    });
    return response.data;
  }

  /**
   * Trigger graph embedding computation (Node2Vec)
   */
  async computeGraphEmbeddings(courseId?: string, minStrength: number = 0.1): Promise<{
    status: string;
    nodes: number;
    edges: number;
    embeddings_computed: number;
    embeddings_stored: number;
    errors: string[];
  }> {
    const params: Record<string, string> = {
      min_strength: minStrength.toString(),
    };
    if (courseId) params.course_id = courseId;

    const response = await this.http.post('/pattern-of-life/compute-graph-embeddings', {}, { params });
    return response.data;
  }

  /**
   * Train anomaly detection model for an entity
   */
  async trainAnomalyModel(
    entityId: string,
    courseId?: string,
    trainingDays: number = 90
  ): Promise<{ message: string; entity_id: string; training_days: number }> {
    const response = await this.http.post('/pattern-of-life/anomaly-model/train', {
      entity_id: entityId,
      course_id: courseId,
      training_days: trainingDays,
    });
    return response.data;
  }

  /**
   * Detect anomalies for an entity over a date range
   */
  async detectAnomalies(
    entityId: string,
    startDate: string,
    endDate: string,
    courseId?: string
  ): Promise<AnomalyDetectionResponse> {
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    };
    if (courseId) params.course_id = courseId;

    const response = await this.http.get(
      `/pattern-of-life/anomaly-detection/${entityId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Get anomaly summary for an entity
   */
  async getAnomalySummary(
    entityId: string,
    courseId?: string,
    days: number = 30
  ): Promise<AnomalySummary> {
    const params: Record<string, string> = {
      days: days.toString(),
    };
    if (courseId) params.course_id = courseId;

    const response = await this.http.get(
      `/pattern-of-life/anomaly-summary/${entityId}`,
      { params }
    );
    return response.data;
  }

  /**
   * List trained embedding models
   */
  async getEmbeddingModels(modelType?: string): Promise<{ models: EmbeddingModel[]; count: number }> {
    const params: Record<string, string> = {};
    if (modelType) params.model_type = modelType;

    const response = await this.http.get('/pattern-of-life/embedding-models', { params });
    return response.data;
  }

  /**
   * Get exclusion zone polygons for map filtering
   * @param name Optional filter by zone name (e.g., 'Davis-Monthan')
   */
  async getExclusionZones(name?: string): Promise<ExclusionZonesResponse> {
    const params: Record<string, string> = {};
    if (name) params.name = name;

    const response = await this.http.get('/pattern-of-life/exclusion-zones', { params });
    return response.data;
  }

  /**
   * Phase 1: Generate (or fetch cached) baseline-synthesized "expected day"
   * forecast for one entity on one target date.
   *
   * Default `target_date` is tomorrow (UTC) on the server. Pass `refresh: true`
   * to bypass the 12-hour cache and recompute.
   */
  async getExpectedDay(req: ExpectedDayRequest): Promise<ExpectedDayResponse> {
    const response = await this.http.post(
      '/pattern-of-life/predict/expected-day',
      req,
    );
    return response.data;
  }

  /**
   * Phase 2 — rolling backtest accuracy for an entity. Backs the "Quality"
   * KPI cell on the Forecast tab. Returns backtests_run=0 (no error) when
   * the backtest table is empty for this entity.
   */
  async getForecastQuality(
    req: ForecastQualityRequest,
  ): Promise<ForecastQualityResponse> {
    const params = new URLSearchParams({ entity_id: req.entity_id });
    if (req.course_id) params.set('course_id', req.course_id);
    if (req.forecaster) params.set('forecaster', req.forecaster);
    if (req.forecaster_version)
      params.set('forecaster_version', req.forecaster_version);
    if (req.window_days != null)
      params.set('window_days', String(req.window_days));
    const response = await this.http.get(
      `/pattern-of-life/predict/quality?${params.toString()}`,
    );
    return response.data;
  }

  /**
   * Phase 2 — run a backtest (single day or date range). Persists to
   * entity_prediction_backtests so the next call to getForecastQuality()
   * reflects the new results.
   */
  async runBacktest(
    req: BacktestRequest,
  ): Promise<BacktestBatchResponse> {
    const response = await this.http.post(
      '/pattern-of-life/predict/backtest',
      req,
    );
    return response.data;
  }

  /**
   * Phase 4 (lightweight) — current state of an entity and likely next
   * stops from the pre-computed Markov chain. Backed by latest dwell/track
   * in the DB; poll every ~60s.
   */
  async getRightNow(req: RightNowRequest): Promise<RightNowResponse> {
    const params = new URLSearchParams({ entity_id: req.entity_id });
    if (req.course_id) params.set('course_id', req.course_id);
    if (req.top_n != null) params.set('top_n', String(req.top_n));
    const response = await this.http.get(
      `/pattern-of-life/predict/right-now?${params.toString()}`,
    );
    return response.data;
  }
}

// Timeline types
export interface TimelineEntity {
  entity_id: string;
  course_id: string;
  devices: { device_type: string; device_ref_id: number }[];
}

export interface TimelineDwell {
  entity_id: string;
  dwell_id: number;
  start_time: string;
  end_time: string | null;
  lat: number;
  lon: number;
  location_name: string;
  device_type: string;
  duration_seconds: number;
  place_type: string | null;
}

export interface TimelineResponse {
  entities: TimelineEntity[];
  dwells: TimelineDwell[];
  time_range: { start: string; end: string };
  entity_count: number;
  dwell_count: number;
}

export interface TrackPosition {
  entity_id: string;
  device_type: string;
  lat: number;
  lon: number;
  timestamp: string;
  speed: number | null;
  bearing: number | null;
}

export interface TracksAtTimeResponse {
  target_time: string;
  window_seconds: number;
  positions: TrackPosition[];
  count: number;
}

// ============================================================================
// Embedding & Similarity Search Types
// ============================================================================

export interface SimilarEntity {
  entity_id: string;
  entity_type: string | null;
  similarity: number;
  routine_score: number;
  mobility_score: number;
  social_score: number;
  total_dwells: number;
  unique_locations: number;
}

export interface SimilarEntitiesResponse {
  entity_id: string;
  course_id: string;
  similar_entities: SimilarEntity[];
  count: number;
  message?: string;
}

export interface SimilarTrajectory {
  sequence_id: number;
  entity_id: string;
  sequence_length: number;
  occurrence_count: number;
  locations: { id: string; name: string; lat: number; lon: number; order: number }[];
  similarity: number;
}

export interface SimilarTrajectoriesResponse {
  sequence_id: number;
  similar_sequences: SimilarTrajectory[];
  count: number;
}

export interface SimilarLocation {
  dwell_id: number;
  place_name: string | null;
  place_type: string | null;
  address: string | null;
  latitude: number;
  longitude: number;
  total_visits: number;
  average_duration: number;
  students: string[] | null;
  similarity: number;
}

export interface SimilarLocationsResponse {
  dwell_id: number;
  similar_locations: SimilarLocation[];
  count: number;
}

export interface EmbeddingStats {
  status: string;
  course_id: string | null;
  pol_summaries: {
    total: number;
    with_embedding: number;
    coverage: number;
  };
  location_sequences: {
    total: number;
    with_embedding: number;
    coverage: number;
  };
  dwell_locations?: {
    total: number;
    with_embedding: number;
    coverage: number;
    note?: string;
  };
}

export interface DailyAnomalyResult {
  date: string;
  is_anomalous: boolean;
  reconstruction_error: number | null;
  anomaly_score: number;
  threshold?: number;
  threshold_percentile?: number;
  method?: string;
  z_scores?: {
    dwells: number;
    active_hours: number;
  };
  context: {
    total_dwells: number;
    active_hours_count: number;
    active_hours?: number[];
  };
}

export interface AnomalyDetectionResponse {
  entity_id: string;
  start_date: string;
  end_date: string;
  total_days: number;
  anomalous_days_count: number;
  anomaly_rate: number;
  daily_results: DailyAnomalyResult[];
  anomalous_days: DailyAnomalyResult[];
}

export interface AnomalySummary {
  entity_id: string;
  period_days: number;
  total_days_analyzed: number;
  anomalous_days: number;
  anomaly_rate: number;
  avg_anomaly_score: number;
  max_anomaly_score: number;
  avg_reconstruction_error: number;
  recent_anomalies: {
    date: string;
    anomaly_score: number;
    reconstruction_error: number;
  }[];
}

export interface EmbeddingModel {
  id: number;
  model_name: string;
  model_type: string;
  version: string;
  architecture: {
    input_dim: number;
    hidden_dim: number;
    latent_dim: number;
  };
  training: {
    days_used: number;
    final_loss: number | null;
  };
  thresholds: {
    percentile_95: number | null;
    percentile_99: number | null;
  };
  is_active: boolean;
  created_at: string;
  trained_at: string;
}

export interface ExclusionZone {
  id: number;
  name: string;
  geojson: GeoJSON.Polygon;
  centroid?: { lat: number; lon: number };
}

export interface ExclusionZonesResponse {
  zones: ExclusionZone[];
  count: number;
}

// ===========================================================================
// Prediction / Forecast (Phase 1: Expected Day)
// ===========================================================================

export interface ExpectedDayVisit {
  pattern_id: number | null;
  location_id: string | null;
  location_name: string | null;
  location_coordinates: { lat: number; lon: number } | null;
  start_time: string;
  end_time: string;
  frequency: string | null;
  adherence_rate: number;
  confidence: number;
  /** Unified "fires today" probability (0..1) — primary value rendered in the UI pill. */
  dow_likelihood: number;
  /** Number of target-DOW occurrences behind the rate (= occurrence_count). */
  dow_sample_size: number;
  occurrence_count: number;
  avg_duration_minutes: number | null;
  avg_arrival_variance_minutes: number | null;
}

export interface ExpectedDayTransit {
  route_id: string | null;
  route_name: string | null;
  origin_name: string | null;
  origin_coordinates: { lat: number; lon: number } | null;
  destination_name: string | null;
  destination_coordinates: { lat: number; lon: number } | null;
  peak_hour: number | null;
  avg_duration_minutes: number | null;
  avg_distance_km: number | null;
  consistency: number;
  confidence: number;
  /** Unified "fires today" probability (0..1) — primary value rendered in the UI pill. */
  dow_likelihood: number;
  /** Trip count on the target DOW (numerator behind dow_likelihood). */
  dow_trip_count: number;
  usage_count: number;
  behavioral_tags: string[];
}

export interface ExpectedDayCoPresence {
  other_entity_id: string;
  correlation_type: string;
  correlation_strength: number;
  confidence: number;
  /** Observed fraction of past target-DOW days this pair was actually co-present. 0..1 */
  dow_likelihood: number;
  /** How many days of the target DOW fell in the lookback window. <2 means no signal. */
  dow_sample_size: number;
  active_hours: number[];
  avg_overlap_minutes: number | null;
  shared_locations: { name?: string; lat?: number; lon?: number; count?: number }[];
}

export interface ExpectedDayDigital {
  pattern_category: string;
  pattern_name: string;
  pattern_description: string | null;
  peak_hour: number | null;
  /** Dominant day-of-week if the pattern is DOW-skewed (e.g. "Monday"); null when agnostic. */
  peak_day: string | null;
  confidence: number;
  /** Unified "fires today" probability (0..1) — primary value rendered in the UI pill. */
  dow_likelihood: number;
  usage_frequency: number | null;
  days_active: number | null;
}

export interface ExpectedDaySlot {
  hour: number;
  presence_probability: number;
  primary_activity: string | null;
  activity_kind: 'visit' | 'transit' | 'digital' | 'idle' | 'mixed' | 'copresence';
  visit_pattern_ids: number[];
  transit_route_ids: string[];
  digital_categories: string[];
  copresence_count: number;
  confidence: number;
}

export interface ExpectedDayMobility {
  median_distance_km: number;
  iqr_distance_km: number;
  median_unique_places: number;
  iqr_unique_places: number;
  median_dwell_count: number;
  iqr_dwell_count: number;
  median_active_hours: number;
  samples: number;
}

export interface ExpectedDayResponse {
  entity_id: string;
  course_id: string | null;
  forecast_date: string;
  day_of_week: string;
  is_in_future: boolean;
  prediction_confidence: number;
  data_maturity: number;
  slots: ExpectedDaySlot[];
  expected_visits: ExpectedDayVisit[];
  expected_transits: ExpectedDayTransit[];
  expected_copresence: ExpectedDayCoPresence[];
  expected_digital: ExpectedDayDigital[];
  expected_mobility: ExpectedDayMobility;
  predicted_active_hours: number;
  predicted_total_dwells: number;
  predicted_distance_km: number;
  predicted_places_count: number;
  sources_used: Record<string, number>;
  notes: string[];
}

export interface ExpectedDayRequest {
  entity_id: string;
  target_date?: string;       // YYYY-MM-DD; defaults to tomorrow UTC
  course_id?: string;
  lookback_days?: number;      // default 30, 7..180
  refresh?: boolean;
}

// ---- Phase 2 ----

export interface ForecastQualityRequest {
  entity_id: string;
  course_id?: string;
  forecaster?: string;          // default 'expected_day'
  forecaster_version?: string;  // optional filter
  window_days?: number;         // default 30, 7..180
}

export interface ForecastQualityByDay {
  target_date: string | null;
  composite_accuracy: number | null;
}

export interface ForecastQualityResponse {
  entity_id: string;
  course_id: string | null;
  backtests_run: number;
  rolling_accuracy: number | null;
  kpi_accuracy: number | null;
  visit_recall: number | null;
  visit_precision: number | null;
  timeline_agreement: number | null;
  copresence_recall: number | null;
  last_backtest_at: string | null;
  window_start: string | null;
  window_end: string | null;
  by_day: ForecastQualityByDay[];
  note: string | null;
}

export interface BacktestRequest {
  entity_id: string;
  course_id?: string;
  target_date?: string;         // YYYY-MM-DD; mutually exclusive with range
  start_date?: string;          // YYYY-MM-DD inclusive
  end_date?: string;            // YYYY-MM-DD inclusive
  forecaster?: string;          // default 'expected_day'
  forecaster_version?: string;  // default 'baseline_synth_v1'
  skip_existing?: boolean;      // default true
  persist?: boolean;            // default true
}

export interface BacktestResultResponse {
  entity_id: string;
  course_id: string | null;
  target_date: string;
  baseline_cutoff: string;
  forecaster: string;
  forecaster_version: string;
  predicted_kpis: Record<string, number | null>;
  actual_kpis: Record<string, number | null>;
  per_kpi_errors: Record<string, number | null>;
  composite_accuracy: number | null;
  kpi_accuracy: number | null;
  visit_recall: number | null;
  visit_precision: number | null;
  timeline_agreement: number | null;
  copresence_recall: number | null;
  diagnostics: Record<string, unknown>;
  inconclusive: boolean;
  inconclusive_reason: string | null;
}

export interface BacktestBatchResponse {
  entity_id: string;
  course_id: string | null;
  forecaster: string;
  forecaster_version: string;
  range_start: string;
  range_end: string;
  results: BacktestResultResponse[];
  skipped: number;
  note: string | null;
}

// ---- Phase 4 (lightweight) ----

export interface RightNowRequest {
  entity_id: string;
  course_id?: string;
  top_n?: number; // default 3
}

export interface RightNowPosition {
  source: 'dwell' | 'track' | 'unknown';
  observed_at: string | null;
  age_minutes: number | null;
  location_id: string | null;
  location_name: string | null;
  lat: number | null;
  lon: number | null;
  cluster: string | null;
  is_currently_dwelling: boolean;
  dwell_started_at: string | null;
  // Honest motion summary — render this instead of inferring transit from
  // `source === 'track'`. A parked phone sending GPS heartbeats is
  // `motion_state: 'stationary'`, not `'in_transit'`.
  motion_state?: 'dwelling' | 'stationary' | 'in_transit' | 'unknown';
  // Meters from this fix to the most recent dwell centroid. Useful for
  // tooltips and debugging "why is this stationary not in_transit".
  distance_from_last_dwell_m?: number | null;
}

export interface RightNowNextStop {
  cluster: string;
  probability: number;
  location_hint: string | null;
}

export interface RightNowExpectation {
  hour: number;
  activity_kind: string;
  primary_activity: string;
  confidence: number;
  presence_probability: number;
}

export interface RightNowResponse {
  entity_id: string;
  course_id: string | null;
  snapshot_at: string;
  current_position: RightNowPosition;
  expected_now: RightNowExpectation | null;
  likely_next_stops: RightNowNextStop[];
  surprise_signal: number | null;
  note: string | null;
}

export const polService = new PolService();
export default polService;

